import { forwardRef } from 'react'

import { tv, type VariantProps } from 'tailwind-variants'

import { cn } from '../../../utils/cn'

export const textVariants = tv(
  {
    /* No base leading utility: line-height comes from the per-size
     --text-*--line-height tokens of the Ømni matrix, which the text-*
     utilities apply and a leading-* utility here would override. Note:
     a font-size class in className (e.g. text-[15px]) makes cn() merge
     out the variant's text-* class INCLUDING its line-height — pass an
     explicit leading-* alongside any font-size override. */
    variants: {
      variant: {
        h1: 'font-heading text-h1 font-semibold tracking-tight',
        h2: 'font-heading text-h2 font-semibold tracking-tight',
        h3: 'font-heading text-h3 font-semibold tracking-tight',
        h4: 'font-heading text-h4 font-semibold tracking-tight',
        'title-xl': 'font-heading text-title-xl font-medium',
        'title-lg': 'font-heading text-title-lg font-medium',
        'body-xl': 'font-body text-body-xl font-normal',
        'body-lg': 'font-body text-body-lg font-normal',
        'body-md': 'font-body text-body-md font-normal',
        'caption-lg': 'font-body text-caption-lg font-normal',
        'caption-md': 'font-body text-caption-md font-normal',
        'caption-sm': 'font-body text-caption-sm font-normal'
      },
      /* Per-tier weight availability follows the Ømni matrix (see the Weights
       story in text.stories.tsx); not yet enforced in types. */
      weight: {
        regular: 'font-normal',
        medium: 'font-medium',
        semibold: 'font-semibold',
        bold: 'font-bold'
      }
    },
    defaultVariants: {
      variant: 'body-md'
    }
  },
  /* tv()'s internal twMerge runs with the default config, which doesn't know
   the Ømni font-size tokens; every consumer pipes the output through cn(),
   whose registry does — so the internal pass is pure waste (same as badge). */
  { twMerge: false }
)

export type TypographyVariant = NonNullable<
  VariantProps<typeof textVariants>['variant']
>

type TextElement =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'p'
  | 'span'
  | 'div'
  | 'strong'
  | 'em'
  | 'small'
  | 'label'

const defaultElementMap: Record<TypographyVariant, TextElement> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  'title-xl': 'h3',
  'title-lg': 'h4',
  'body-xl': 'p',
  'body-lg': 'p',
  'body-md': 'p',
  'caption-lg': 'small',
  'caption-md': 'small',
  'caption-sm': 'small'
}

export type TextProps = VariantProps<typeof textVariants> & {
  as?: TextElement
  truncate?: boolean
  className?: string
  children?: React.ReactNode
} & Omit<React.HTMLAttributes<HTMLElement>, 'as'> & {
    ref?: React.Ref<HTMLElement>
  }

export const Text = forwardRef<HTMLElement, TextProps>(
  ({ as, variant, weight, truncate, className, ...props }, ref) => {
    const resolvedVariant = variant ?? 'body-md'
    const Component = (as ??
      defaultElementMap[resolvedVariant]) as React.ElementType

    return (
      <Component
        ref={ref}
        data-slot='text'
        className={cn(
          textVariants({ variant, weight }),
          truncate && 'truncate',
          className
        )}
        {...props}
      />
    )
  }
)
Text.displayName = 'Text'
